import { EChart } from "@/lib/EChart";
import type { Infringement, Edr } from "@/types/domain";
import { useThemeColors } from "@/hooks/useThemeColors";
import { useEdrsQuery } from "@/lib/queries";
import { useFilteredInfringements } from "@/hooks/useFilteredInfringements";
import { WidgetShell } from "@/components/dashboard/WidgetShell";

const BUCKET_MS = 15 * 60 * 1000;

const EDR_COLORS = [
  { line: "#06b6d4", area: "rgba(6,182,212,0.12)" },
  { line: "#8b5cf6", area: "rgba(139,92,246,0.12)" },
  { line: "#f59e0b", area: "rgba(245,158,11,0.12)" },
  { line: "#10b981", area: "rgba(16,185,129,0.12)" },
];

function bucketKey(isoDate: string): string {
  const t = new Date(isoDate).getTime();
  return new Date(Math.floor(t / BUCKET_MS) * BUCKET_MS).toISOString();
}

function bucketLabel(isoKey: string): string {
  return new Date(isoKey).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
}

function EdrVolumeChart({ edrs, infringements }: { edrs: Edr[]; infringements: Infringement[] }) {
  const colors = useThemeColors();

  // Collect all unique time buckets across all infringements
  const allBuckets = new Set<string>();
  infringements.forEach((inf) => allBuckets.add(bucketKey(inf.detected)));
  const sortedBuckets = Array.from(allBuckets).sort();
  const timestamps = sortedBuckets.map(bucketLabel);

  // Count detections per EDR per bucket
  const countsByEdr = new Map<string, Map<string, number>>();
  edrs.forEach((edr) => countsByEdr.set(edr.id, new Map()));

  infringements.forEach((inf) => {
    const edr = edrs.find((e) => e.id === inf.edr_id);
    if (!edr) return; // No EDR found for this infringement
    const edrMap = countsByEdr.get(edr.id);
    if (!edrMap) return;
    const key = bucketKey(inf.detected);
    edrMap.set(key, (edrMap.get(key) ?? 0) + 1);
  });

  const series = edrs.map((edr, idx) => {
    const edrMap = countsByEdr.get(edr.id) ?? new Map();
    const data = sortedBuckets.map((b) => edrMap.get(b) ?? 0);
    const palette = EDR_COLORS[idx % EDR_COLORS.length];
    return {
      name: edr.label,
      type: "line",
      smooth: true,
      symbol: "circle",
      symbolSize: 4,
      lineStyle: { color: palette.line, width: 2 },
      itemStyle: { color: palette.line },
      areaStyle: {
        color: {
          type: "linear",
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: palette.area },
            { offset: 1, color: "rgba(0,0,0,0)" },
          ],
        },
      },
      data,
    };
  });

  const option = {
    backgroundColor: "transparent",
    grid: { left: "4%", right: "4%", top: "14%", bottom: "10%", containLabel: true },
    legend: {
      top: 0,
      left: "center",
      textStyle: { color: colors.textSecondary, fontSize: 11, fontFamily: "Inter, sans-serif" },
      itemWidth: 16,
      itemHeight: 10,
      icon: "circle",
    },
    tooltip: {
      trigger: "axis",
      backgroundColor: colors.surfaceElevated,
      borderColor: colors.border,
      borderWidth: 1,
      textStyle: { color: colors.textPrimary, fontSize: 12 },
      formatter: (params: any) => {
        let content = `<div style="font-weight:600;margin-bottom:6px;font-size:11px;">${params[0].axisValue}</div>`;
        params.forEach((item: any) => {
          content += `<div style="display:flex;align-items:center;gap:8px;margin-top:4px;">
            <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${item.color}"></span>
            <span style="flex:1">${item.seriesName}</span>
            <span style="font-weight:600">${item.value}</span>
          </div>`;
        });
        return content;
      },
    },
    xAxis: {
      type: "category",
      data: timestamps,
      boundaryGap: false,
      axisLine: { lineStyle: { color: colors.border } },
      axisTick: { show: false },
      axisLabel: {
        color: colors.textMuted,
        fontSize: 10,
        fontFamily: "JetBrains Mono, monospace",
        interval: Math.max(0, Math.floor(timestamps.length / 6)),
      },
      splitLine: { show: false },
    },
    yAxis: {
      type: "value",
      minInterval: 1,
      splitLine: { lineStyle: { color: colors.border, width: 1 } },
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: colors.textMuted, fontSize: 10, fontFamily: "Inter, sans-serif" },
    },
    series,
  };

  if (infringements.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-muted">
        No data available
      </div>
    );
  }

  return <EChart option={option} style={{ height: "100%" }} />;
}

export function EdrVolumeWidget() {
  const { data: edrs, isPending: edrsPending } = useEdrsQuery();
  const { infringements, isPending: infPending } = useFilteredInfringements();

  const isPending = edrsPending || infPending;

  return (
    <WidgetShell title="EDR Activity">
      <div className="flex-1 px-2 pb-2">
        {isPending ? (
          <div className="h-full flex items-center justify-center text-sm text-muted animate-pulse">
            Loading…
          </div>
        ) : (
          <EdrVolumeChart edrs={edrs ?? []} infringements={infringements} />
        )}
      </div>
    </WidgetShell>
  );
}
